import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaBalanceScale,
    FaBitcoin,
    FaBookOpen,
    FaBrain,
    FaChartPie,
    FaExternalLinkAlt,
    FaGavel,
    FaGlobeAmericas,
    FaHome,
    FaLeaf,
    FaUserShield
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChartLineOutline,
    TiChevronRight,
    TiFlash,
    TiNews,
    TiRssOutline,
    TiWorldOutline
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const Blog = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeCategory, setActiveCategory] = useState("All");

  const sectionRefs = useRef([]);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.05,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const blogPosts = [
    {
      title: "Investing in the Digital Age: Navigating Cryptocurrencies and Blockchain",
      excerpt: "Assessing the technical underpinnings of decentralized ledgers and their disruptive impact on traditional asset class liquidity and settlement systems.",
      category: "Business & Entrepreneurship",
      date: "Jan 12, 2026",
      image: "https://static.wixstatic.com/media/11062b_7258b42542d4465e859cd0ba37797c29~mv2.jpeg/v1/fill/w_1200,h_630,q_90/blockchain.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/investing-in-the-digital-age-navigating-cryptocurrencies-and-blockchain",
      icon: <FaBitcoin />,
      featured: true
    },
    {
      title: "Cyberbullying and the Law: Understanding Your Rights and Legal Remedies",
      excerpt: "Analyzing the intersection of digital harassment protocols and existing tort law frameworks to establish robust victim restitution pathways.",
      category: "Politics & Current Events",
      date: "Jan 10, 2026",
      image: "https://static.wixstatic.com/media/11062b_d37433819a4e405892c43eb1c905cbd6~mv2.jpg/v1/fill/w_800,h_600,q_90/cyberbullying.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/cyberbullying-and-the-law-understanding-your-rights-and-legal-remedies",
      icon: <FaUserShield />
    },
    {
      title: "Maximizing Your Property Investment: Tips for Renovating for Profit",
      excerpt: "Optimizing ROI through strategic capital injection into distressed assets and leveraging market-timing heuristics for maximum property appreciation.",
      category: "Personal Finance",
      date: "Jan 08, 2026",
      image: "https://static.wixstatic.com/media/11062b_d6302140499841059c0b2af7b91a1ba3~mv2.jpg/v1/fill/w_800,h_600,q_90/realestate.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/maximizing-your-property-investment-tips-for-renovating-for-profit",
      icon: <FaHome />
    },
    {
      title: "Environmental, Social, and Governance (ESG) Investing: A Sustainable Approach",
      excerpt: "Integrating ESG sustainability metrics into risk-adjusted valuation models to drive long-term alpha generation and ethical capital allocation.",
      category: "Retirement, Planning & Savings",
      date: "Jan 05, 2026",
      image: "https://static.wixstatic.com/media/52beb4602a6048758ee3cbf0eb99d078.jpg/v1/fill/w_800,h_600,q_90/esg.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/environmental-social-and-governance-esg-investing-a-sustainable-approach",
      icon: <FaLeaf />
    },
    {
      title: "The Psychology of Investing: Overcoming Behavioural Biases",
      excerpt: "Analyzing the cognitive heuristics and emotional biases that drive suboptimal investment decisions and implementing loss-aversion mitigation protocols.",
      category: "Retirement, Planning & Savings",
      date: "Jan 02, 2026",
      image: "https://static.wixstatic.com/media/nsplsh_c6f6d3a084cf4018a1c4f6445dec1fb1~mv2.jpg/v1/fill/w_800,h_600,q_90/psychology.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/the-psychology-of-investing-overcoming-behavioural-biases",
      icon: <FaBrain />
    },
    {
      title: "Case Studies in Legal Ethics: Lessons Learned from Real-Life Examples",
      excerpt: "A deep dive into the normative frameworks governing legal practitioners, utilizing empirical case studies to delineate ethical boundaries in complex litigation.",
      category: "Business & Entrepreneurship",
      date: "Dec 30, 2025",
      image: "https://static.wixstatic.com/media/11062b_367f3e6f9d0b4ab09d22cc58cfe1be15~mv2.jpeg/v1/fill/w_800,h_600,q_90/ethics.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/case-studies-in-legal-ethics-lessons-learned-from-real-life-examples",
      icon: <FaGavel />
    },
    {
      title: "Impact of Inflation on Investments: Strategies for Preserving Purchasing Power",
      excerpt: "Implementing multidimensional hedging strategies using inflation-protected securities to preserve real parity amidst aggressive monetary expansion.",
      category: "Personal Finance",
      date: "Dec 28, 2025",
      image: "https://static.wixstatic.com/media/2675df_e6be88e4af58497bb5dcd267e755ef12~mv2.jpg/v1/fill/w_800,h_600,q_90/inflation.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/impact-of-inflation-on-investments-strategies-for-preserving-purchasing-power",
      icon: <TiChartLineOutline />
    },
    {
      title: "The Role of REITs in a Diversified Portfolio",
      excerpt: "Enhancing portfolio beta through fractionalised ownership of institutional-grade real estate assets within a diversified, liquid investment vehicle.",
      category: "Retirement, Planning & Savings",
      date: "Dec 25, 2025",
      image: "https://static.wixstatic.com/media/nsplsh_6e45767a53584249686955~mv2_d_5154_3436_s_4_2.jpg/v1/fill/w_800,h_600,q_90/reits.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/the-role-of-real-estate-investment-trusts-reits-in-a-diversified-portfolio",
      icon: <FaChartPie />
    },
    {
      title: "Real Estate Investing 101: Getting Started with Rental Properties",
      excerpt: "Establishing scalable cash-flow models via diversified rental portfolios and comprehensive tenant lifecycle management strategies.",
      category: "Personal Finance",
      date: "Dec 22, 2025",
      image: "https://static.wixstatic.com/media/nsplsh_ce0fd7378c1148bba5bc5509d061f763~mv2.jpg/v1/fill/w_800,h_600,q_90/rental.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/real-estate-investing-101-getting-started-with-rental-properties",
      icon: <FaHome />
    },
    {
      title: "Navigating the Real Estate Market as a First-Time Homebuyer",
      excerpt: "Decrypting the residential acquisition pipeline, from preliminary credit-worthiness assessment to final escrow reconciliation and closing logistics.",
      category: "Personal Finance",
      date: "Dec 20, 2025",
      image: "https://static.wixstatic.com/media/11062b_7c9359e1c03b44e8bfec3ddc7470a136~mv2.jpg/v1/fill/w_800,h_600,q_90/homebuyer.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/navigating-the-real-estate-market-as-a-first-time-homebuyer",
      icon: <FaBalanceScale />
    },
    {
      title: "Buying a Fixer-Upper: Pros, Cons, and Tips for Success",
      excerpt: "Mitigating operational risk in residential redevelopment by implementing robust cost-benefit analysis and stringent contractor oversight protocols.",
      category: "Frugal Living & Budgeting",
      date: "Dec 15, 2025",
      image: "https://static.wixstatic.com/media/edcb2e19a98747b6a0cf1cadbbae0a3e.jpg/v1/fill/w_800,h_600,q_90/fixerupper.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/buying-a-fixer-upper-pros-cons-and-tips-for-success",
      icon: <FaGlobeAmericas />
    },
    {
      title: "Navigating Volatility: Tips for Investing During Market Uncertainty",
      excerpt: "Utilizing statistical arbitrage and defensive asset allocation to navigate non-linear price movements in high-beta market environments.",
      category: "Credit Scores & Debt Management",
      date: "Dec 10, 2025",
      image: "https://static.wixstatic.com/media/nsplsh_78476c7450615831466559~mv2_d_3935_2339_s_2.jpg/v1/fill/w_800,h_600,q_90/volatility.webp",
      url: "https://moolah0161.wixsite.com/moo-lah/post/navigating-volatility-tips-for-investing-during-market-uncertainty",
      icon: <FaUserShield />
    }
  ];

  const categories = ["All", ...new Set(blogPosts.map(p => p.category))];
  const filteredPosts = activeCategory === "All" ? blogPosts : blogPosts.filter(p => p.category === activeCategory);
  const featuredPost = blogPosts.find(p => p.featured);

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/blog-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="blogHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={40}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Intellectual Sovereignty"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Intelligence for the Future powered by Moo-Lah
            </motion.p>
          </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Blog Hub */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Featured Post Spotlight */}
        {featuredPost && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative w-full mb-32 group cursor-pointer"
            onClick={() => window.open(featuredPost.url, '_blank')}
          >
            <div className="relative h-[450px] md:h-[600px] w-full rounded-[4rem] overflow-hidden border border-white/10 flex flex-col md:flex-row shadow-2xl">
              <div className="w-full md:w-3/5 h-1/2 md:h-full overflow-hidden">
                <img 
                  src={featuredPost.image} 
                  alt={featuredPost.title} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="w-full md:w-2/5 h-1/2 md:h-full bg-gradient-to-br from-indigo-900/40 to-black p-8 md:p-16 flex flex-col justify-center backdrop-blur-3xl border-l border-white/10">
                <div className="flex items-center gap-3 text-indigo-400 font-bold uppercase tracking-[0.3em] text-[10px] mb-8">
                  <TiFlash className="text-xl" />
                  Featured Insight
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-10 line-clamp-4 italic border-l-2 border-indigo-500 pl-6">
                  "{featuredPost.excerpt}"
                </p>
                <div className="flex items-center justify-between mt-auto pt-8 border-t border-white/10">
                   <div className="flex items-center gap-2 text-indigo-400 font-bold text-[10px] uppercase tracking-widest">
                      <FaExternalLinkAlt /> Read on Moo-Lah
                   </div>
                   <div className="text-[10px] text-gray-500 font-bold uppercase">{featuredPost.date}</div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Filter */}
        <div className="mb-20 flex flex-wrap justify-center gap-4">
           {categories.map((cat, index) => (
             <button
               key={index}
               onClick={() => setActiveCategory(cat)}
               className={`px-8 py-3 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all border ${
                 activeCategory === cat 
                   ? "bg-indigo-600 border-indigo-500 text-black shadow-[0_0_30px_rgba(79,70,229,0.3)]" 
                   : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
               }`}
             >
               {cat}
             </button>
           ))}
        </div>

        {/* Article Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-48">
          <AnimatePresence mode="popLayout">
            {filteredPosts.filter(p => !p.featured || activeCategory !== "All").map((post, index) => (
              <motion.div
                layout
                key={post.title}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="group relative bg-white/5 border border-white/10 rounded-[3rem] overflow-hidden hover:border-indigo-500/40 transition-all cursor-pointer flex flex-col"
                onClick={() => window.open(post.url, '_blank')}
                ref={(el) => (sectionRefs.current[index] = el)}
              >
                <div className="h-64 overflow-hidden relative">
                   <img 
                     src={post.image} 
                     alt={post.title} 
                     className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-60 group-hover:opacity-100"
                   />
                   <div className="absolute top-6 right-6 px-4 py-2 bg-black/60 backdrop-blur-md rounded-xl text-[8px] font-bold text-indigo-400 uppercase tracking-widest border border-indigo-500/30">
                      {post.category}
                   </div>
                </div>
                
                <div className="p-10 flex-grow flex flex-col">
                   <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-xl text-indigo-400">
                         {post.icon}
                      </div>
                      <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest">{post.date}</div>
                   </div>
                   
                   <h3 className="text-xl font-bold text-white mb-6 leading-tight group-hover:text-indigo-400 transition-colors">
                      {post.title}
                   </h3>
                   
                   <p className="text-gray-300 text-xs leading-relaxed line-clamp-3 mb-8">
                      {post.excerpt}
                   </p>

                   <div className="mt-auto flex items-center justify-between text-indigo-400 pt-6 border-t border-white/5">
                      <span className="text-[8px] font-bold uppercase tracking-[0.2em]">Deep Intel</span>
                      <TiChevronRight className="text-xl group-hover:translate-x-2 transition-transform" />
                   </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Moo-Lah Integration Hub */}
        <div className="mb-48 p-1 rounded-[4rem] bg-gradient-to-br from-indigo-500/20 to-transparent border border-white/10 overflow-hidden">
           <div className="bg-black/80 backdrop-blur-3xl p-16 md:p-24 flex flex-col md:flex-row items-center gap-20">
              <div className="w-full md:w-1/2">
                 <div className="flex items-center gap-3 text-indigo-400 font-bold uppercase tracking-[0.4em] text-[10px] mb-8">
                    <TiWorldOutline className="text-2xl" />
                    Global Intelligence Syndicate
                 </div>
                 <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-10">
                    Moo-Lah <span className="text-indigo-500 font-zentry italic uppercase text-7xl block mt-2">Intelligence</span>
                 </h2>
                 <p className="text-gray-300 text-lg leading-relaxed mb-12">
                   Zero-Blockchain partners with Moo-Lah to provide institutional-grade financial intelligence, 
                   legal research, and market heuristics directly to our ecosystem participants.
                 </p>
                 <GlassButton
                   variant="indigo"
                   size="sm"
                   className="mt-4"
                   onClick={() => window.open('https://moolah0161.wixsite.com/moo-lah', '_blank')}
                 >
                   <div className="flex items-center gap-4 text-[12px] uppercase tracking-[0.2em]">
                     Explore the Full platform <TiArrowRight className="text-2xl" />
                   </div>
                 </GlassButton>
              </div>
              <div className="w-full md:w-1/2 relative h-[300px] md:h-[500px] flex items-center justify-center">
                 <div className="absolute inset-0 bg-indigo-500/10 rounded-full blur-[100px] animate-pulse" />
                 <div className="relative w-72 h-72 rounded-[3.5rem] bg-white/5 border border-white/10 flex items-center justify-center text-8xl text-indigo-400 shadow-2xl">
                    <TiRssOutline />
                    <div className="absolute -top-4 -right-4 w-24 h-24 rounded-3xl bg-indigo-600 border border-indigo-500 flex items-center justify-center text-black text-4xl shadow-xl animate-bounce">
                       <TiChartLineOutline />
                    </div>
                 </div>
              </div>
           </div>
        </div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative rounded-[4rem] overflow-hidden border border-white/10 p-1"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-blue-900/40 to-black" />
          <div className="absolute inset-0 bg-black/90 backdrop-blur-3xl" />
          
          <div className="relative z-10 py-32 px-8 text-center max-w-4xl mx-auto">
            <div className="w-24 h-24 rounded-[2.5rem] bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center text-5xl text-indigo-400 mx-auto mb-12 shadow-[0_0_50px_rgba(79,70,229,0.2)]">
              <TiNews />
            </div>
            <h3 className="text-4xl md:text-5xl font-bold text-white mb-10 leading-tight">
              Expand Your <span className="font-zentry italic text-indigo-400 text-6xl uppercase tracking-wider block mt-2">Intelligence</span>
            </h3>
            <p className="text-gray-300 mb-16 text-lg leading-relaxed max-w-2xl mx-auto">
              Join 15,000+ ecosystem members receiving real-time insights from across the Zero-Blockchain and Moo-Lah networks.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <GlassButton
                variant="indigo"
                className="px-12 py-6"
                onClick={() => {}}
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  Join Newsletter <TiArrowRight className="text-2xl" />
                </div>
              </GlassButton>
              
              <GlassButton
                variant="gray"
                className="px-12 py-6"
                onClick={() => {}}
              >
                <div className="flex items-center justify-center gap-3 text-lg">
                  Download Reports <TiFlash className="text-2xl text-indigo-400" />
                </div>
              </GlassButton>
            </div>

            <div className="mt-24 flex flex-wrap justify-center gap-12 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaBookOpen /> FINANCIAL INTEL</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaGavel /> LEGAL FRAMEWORKS</div>
               <div className="flex items-center gap-2 font-bold tracking-widest text-[9px]"><FaChartPie /> MARKET HEURISTICS</div>
            </div>
          </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-indigo-500/30 rounded-full shadow-lg shadow-indigo-500/10 cursor-pointer hover:bg-black/60 hover:border-indigo-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-indigo-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Blog;
